import React from 'react'
import { router } from '@inertiajs/react'
import OperatorLayout from '@/layouts/OperatorLayout'
import { HelpTooltip } from '@/components/HelpTooltip'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ArcGauge } from '@/components/snapshot/ArcGauge'
import { MonthCard, type MonthBand } from '@/components/snapshot/MonthCard'
import { YearPulse } from '@/components/snapshot/YearPulse'
import {
  StlyAvailabilityBanner,
  type StlyMeta,
} from '@/components/snapshot/StlyAvailabilityBanner'
import ManagedAtLeadCard from '@/components/groups/ManagedAtLeadCard'
import UnitScopeNote from '@/components/groups/UnitScopeNote'
import { groupHotelCount, isDeferredScope, leadLabel } from '@/lib/reporting-scope'
import { formatMoney } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { SharedProps } from '@/types/shared'

interface SnapshotData {
  months: MonthBand[]
  asOfDate: string
  stlyAnchor: string
  currency: string
  roomTypeId: number | null
  totals: { otb: number; stly: number; lyEnd: number }
  stlyMeta: StlyMeta
}

interface SnapshotProps extends SharedProps {
  /**
   * Null for a grouped MEMBER, deliberately: the forward report describes a revenue unit, and this
   * hotel's share of one is not a report. It is NOT the zeroed shape — that would read as a hotel
   * with no bookings (Operator::SnapshotReportsController#member_snapshot).
   */
  snapshot: SnapshotData | null
  roomTypes: { id: number; name: string }[]
  filters: { roomTypeId: number | null }
}

function formatLongDate(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

export default function Snapshot({
  currentUser,
  property,
  properties,
  snapshot,
  roomTypes,
  filters,
  revenueBasis,
  reportingScope,
}: SnapshotProps) {
  const basePath = `${property.basePath}/reports/snapshot`

  // A MEMBER has no forward report of its own. Point at the lead's — do not render a 12-month grid
  // of zeros, which is the exact misreading the backend's null is there to prevent.
  if (snapshot == null || isDeferredScope(reportingScope)) {
    return (
      <OperatorLayout title="Snapshot" currentUser={currentUser} property={property} properties={properties}>
        <div className="space-y-6">
          <header className="animate-slide-in" style={{ animationDelay: '0ms' }}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-700">
              Forward Almanac · 12 Months
            </p>
            <div className="mt-1 flex items-center gap-2">
              <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">Snapshot</h1>
              <HelpTooltip content="A forward-looking view of how the next 12 months are pacing, against this same point last year and against how those months actually finished." />
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {reportingScope
                ? 'This combined inventory is reported once, at its lead listing'
                : 'No forward figures are available for this property.'}
            </p>
          </header>

          {reportingScope ? (
            <div className="animate-slide-in" style={{ animationDelay: '50ms' }}>
              <ManagedAtLeadCard
                scope={reportingScope}
                what="The forward report"
                detail={`Bookings on ${countHotelsPhrase(reportingScope)} count toward the building, so on-the-books, STLY and last-year-final are reported for the building at ${leadLabel(reportingScope)}.`}
                leadSuffix="/reports/snapshot"
                ctaLabel="Open the building's Snapshot"
              />
            </div>
          ) : (
            <div className="animate-slide-in rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm" style={{ animationDelay: '50ms' }}>
              <p className="text-sm font-medium text-foreground">The forward report couldn’t be built</p>
              <p className="mt-1 text-xs text-muted-foreground">
                These figures are rebuilt on every visit — reloading the page usually brings them back.
              </p>
            </div>
          )}
        </div>
      </OperatorLayout>
    )
  }

  const cur = snapshot.currency

  function setRoomType(value: string) {
    const params: Record<string, string> = {}
    if (value !== 'all') params.room_type_id = value
    router.visit(basePath, { data: params, preserveState: true, preserveScroll: true })
  }

  // Year-level performance (totals): treat like a band so we can reuse the same arc.
  const yearPct =
    snapshot.totals.stly > 0
      ? Math.round((snapshot.totals.otb / snapshot.totals.stly) * 100)
      : null
  const yearTier =
    yearPct == null
      ? null
      : yearPct >= 101
        ? 'excellent'
        : yearPct >= 91
          ? 'good'
          : yearPct >= 71
            ? 'average'
            : 'bad'
  const yearDeltaPct =
    snapshot.totals.stly > 0
      ? ((snapshot.totals.otb - snapshot.totals.stly) / snapshot.totals.stly) * 100
      : null

  return (
    <OperatorLayout title="Snapshot" currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-6">
        {/* HEADER */}
        <header className="animate-slide-in" style={{ animationDelay: '0ms' }}>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-700">
                Forward Almanac · 12 Months
              </p>
              <div className="mt-1 flex items-center gap-2">
                <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">
                  Snapshot
                </h1>
                <HelpTooltip
                  content="A forward-looking view of how the next 12 months are pacing. Compare what's on the books today against this same point last year (STLY) and against how those months actually finished last year (LY final)."
                />
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {property.name} · as of <span className="font-medium text-foreground">{formatLongDate(snapshot.asOfDate)}</span>
                <span className="mx-2 text-slate-300">·</span>
                STLY anchor <span className="font-medium text-foreground">{formatLongDate(snapshot.stlyAnchor)}</span>
                {revenueBasis && (
                  <>
                    <span className="mx-2 text-slate-300">·</span>
                    Revenue basis{' '}
                    <span className="font-medium text-foreground" title={revenueBasis.headline}>
                      {revenueBasis.label}
                    </span>
                  </>
                )}
              </p>
            </div>

            <div className="flex items-end gap-2">
              <label className="flex flex-col gap-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Room Type
                </span>
                <Select
                  value={filters.roomTypeId ? String(filters.roomTypeId) : 'all'}
                  onValueChange={setRoomType}
                >
                  <SelectTrigger className="h-9 w-[200px] rounded-lg border-border/60 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All room types</SelectItem>
                    {roomTypes.map((rt) => (
                      <SelectItem key={rt.id} value={String(rt.id)}>
                        {rt.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            </div>
          </div>
        </header>

        {/* At a LEAD every figure below is pooled across the unit — said before the arc, not after. */}
        <UnitScopeNote
          scope={reportingScope}
          covers="On the books, STLY and last year final"
          className="animate-slide-in"
        />

        {/* YEAR HERO — totals + master arc */}
        <section
          className="animate-slide-in relative overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-white via-white to-teal-50/40 p-6 shadow-sm"
          style={{ animationDelay: '50ms' }}
        >
          {/* decorative wash */}
          <div
            aria-hidden
            className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full bg-teal-100/40 blur-3xl"
          />
          <div className="relative grid items-center gap-8 md:grid-cols-[auto_1fr]">
            <div className="flex flex-col items-center md:items-start">
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                12-Month Performance
              </span>
              <div className="mt-3">
                <ArcGauge pct={yearPct} tier={yearTier} size={180} />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <KpiTile
                label="On the Books"
                value={formatMoney(snapshot.totals.otb, cur)}
                hint="12 months out"
                emphasis
              />
              <KpiTile
                label="STLY"
                value={snapshot.totals.stly > 0 ? formatMoney(snapshot.totals.stly, cur) : '—'}
                hint="364 days ago"
                delta={yearDeltaPct}
              />
              <KpiTile
                label="LY final"
                value={snapshot.totals.lyEnd > 0 ? formatMoney(snapshot.totals.lyEnd, cur) : '—'}
                hint="Realized last year"
              />
            </div>
          </div>
        </section>

        {/* STLY AVAILABILITY (only renders when STLY is fully unavailable) */}
        {snapshot.stlyMeta.monthsAvailable === 0 && snapshot.stlyMeta.trackingStartedAt && (
          <section className="animate-slide-in" style={{ animationDelay: '75ms' }}>
            <StlyAvailabilityBanner meta={snapshot.stlyMeta} propertyName={property.name} />
          </section>
        )}

        {/* YEAR PULSE */}
        <section className="animate-slide-in" style={{ animationDelay: '100ms' }}>
          <YearPulse bands={snapshot.months} currency={cur} />
        </section>

        {/* LEGEND */}
        <section
          className="animate-slide-in flex flex-wrap items-center gap-4 rounded-xl border border-dashed border-border bg-white/60 px-4 py-3 text-[11px]"
          style={{ animationDelay: '120ms' }}
        >
          <span className="font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Performance vs STLY
          </span>
          <TierKey color="bg-emerald-500" label="Excellent" range=">100%" />
          <TierKey color="bg-teal-600" label="Good" range="91–100%" />
          <TierKey color="bg-amber-500" label="Average" range="71–90%" />
          <TierKey color="bg-rose-500" label="Bad" range="≤70%" />
        </section>

        {/* MONTH GRID */}
        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {snapshot.months.map((band, idx) => (
            <div
              key={band.month}
              className="animate-slide-in"
              style={{ animationDelay: `${140 + idx * 30}ms` }}
            >
              <MonthCard band={band} currency={cur} />
            </div>
          ))}
        </section>

        {/* FOOTER NOTE */}
        <footer
          className="animate-slide-in mt-4 rounded-xl bg-slate-50/60 px-5 py-4 text-[11px] leading-relaxed text-muted-foreground"
          style={{ animationDelay: '200ms' }}
        >
          <p>
            <span className="font-semibold uppercase tracking-[0.16em] text-foreground">Method.</span>{' '}
            Revenue is allocated per stay-night from each booking's accommodation total, so bookings
            spanning months contribute proportionally.{' '}
            {revenueBasis ? (
              <>
                Figures are reported on your business's <strong>{revenueBasis.suffix}</strong> basis.{' '}
                {revenueBasis.description} Change this in Business settings.{' '}
              </>
            ) : (
              <>
                Figures are read on your business's reporting basis (gross by default — Business
                settings).{' '}
              </>
            )}
            <strong>STLY</strong> is point-in-time —
            we reconstruct the bookings that were live exactly 364 days ago using <code className="rounded bg-white px-1 py-0.5 text-[10px]">booked_at</code> and
            cancellation timestamps, then aggregate them against the same forward calendar.{' '}
            <strong>Last year final</strong> is what those months ultimately realized.
          </p>
        </footer>
      </div>
    </OperatorLayout>
  )
}

function KpiTile({
  label,
  value,
  hint,
  emphasis,
  delta,
}: {
  label: string
  value: string
  hint?: string
  emphasis?: boolean
  delta?: number | null
}) {
  const deltaSign = delta == null ? null : delta >= 0 ? 'pos' : 'neg'
  return (
    <div
      className={cn(
        'rounded-xl border border-border/40 bg-white/80 p-4 shadow-sm backdrop-blur-sm',
        emphasis && 'border-teal-200 bg-white shadow-md ring-1 ring-teal-100'
      )}
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </p>
      <p
        className={cn(
          'mt-2 font-display tabular-nums tracking-tight',
          emphasis ? 'text-3xl font-semibold text-foreground' : 'text-2xl font-semibold text-foreground'
        )}
      >
        {value}
      </p>
      <div className="mt-1.5 flex items-center justify-between text-[11px]">
        {hint && <span className="text-muted-foreground">{hint}</span>}
        {deltaSign && (
          <span
            className={cn(
              'tabular-nums font-semibold',
              deltaSign === 'pos' ? 'text-emerald-600' : 'text-rose-600'
            )}
          >
            {deltaSign === 'pos' ? '▲' : '▼'} {Math.abs(delta!).toFixed(1)}%
          </span>
        )}
      </div>
    </div>
  )
}

function countHotelsPhrase(scope: NonNullable<SharedProps['reportingScope']>): string {
  const n = groupHotelCount(scope)
  return n === 1 ? '1 listing' : `all ${n} listings`
}

function TierKey({ color, label, range }: { color: string; label: string; range: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-foreground">
      <span className={cn('inline-block h-1.5 w-6 rounded-full', color)} />
      <span className="font-semibold">{label}</span>
      <span className="text-muted-foreground">{range}</span>
    </span>
  )
}
